import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPost, type PostValue } from './client'

/**
 * Fisa unui tip de tichet (v4/api, `App\Settings\Easeedesk\TicketTypeSheet`).
 * Perechile vechi sunt `settings/Easeedesk::ticket_type_overview` si cele
 * cincisprezece capete ale fisei. Forma paginii e inghetata in
 * `settings/easeedesk/__fixtures__/ticketTypeSheet.json`.
 *
 * Capetele pe care fisa doar le IMPRUMUTA de la alte ecrane (prioritati,
 * ceasuri, SLA, flux atribuit, pasi, formulare de update, servicii) stau in
 * clientii acelor ecrane; aici sunt numai cele scrise pentru fisa.
 */
export type SheetPane = 'overview' | 'flow' | 'time' | 'notifications' | 'categories' | 'automations' | 'publishing' | 'history'

export const SHEET_PANES: { key: SheetPane; label: string }[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'flow', label: 'Flow & statuses' },
  { key: 'time', label: 'Time & priority' },
  { key: 'notifications', label: 'Notifications' },
  { key: 'categories', label: 'Categories & services' },
  { key: 'automations', label: 'Automations' },
  { key: 'publishing', label: 'Publishing' },
  { key: 'history', label: 'History' },
]

export interface SheetType {
  ID: number
  type_name: string
  short_name: string
  color_identifier: string
  description: string
  active: number
  automatic: number
  scheme_id: number
  never_used: boolean
}

export interface SheetStatus {
  ID: number
  status_name: string
  status_color: string
  status_type: number
  shared: number
  form_name: string | null
}

export interface SheetStep {
  ID: number
  step_name: string
  status_from: number
  status_to: number
  from_name: string | null
  from_color: string | null
  to_name: string | null
  to_color: string | null
  assign_group_id: number | null
  assign_user_id: number | null
  assign_group_name: string | null
  assign_user_name: string
}

export interface GraphNode {
  id: number
  name: string
  color: string
  kind: number
  form: string | null
  shared: boolean
  column: number
  loops: number
  start?: boolean
  unreachable?: boolean
}

export interface GraphEdge {
  id: number
  from: number
  to: number
  name: string
  handed: string | null
}

export interface SheetSlaRule {
  ID: number
  sla: number
  priority_id: number
  category_id: number
  subcategory_id: number
  operation_id: number
  priority_name: string | null
  priority_color: string | null
  category_name: string | null
  subcategory_name: string | null
  operation_name: string | null
}

export interface SheetAutomation {
  id: number
  kind: 'auto_status' | 'reopen' | 'feedback'
  status: number
  what: string
  detail: string
  active: boolean
  where: string
}

export interface SheetCheck {
  label: string
  ok: boolean
  hint: string
  pane: SheetPane
}

export interface SheetHistoryRow {
  ID: number
  created_at: string
  user_name: string | null
  action_label: string
  entity_label: string
  field_label: string
  old_label: string
  new_label: string
  old_color: string | null
  new_color: string | null
  old_word: string
  new_word: string
}

export interface SheetCatalogItem {
  ID: number
  short_description: string
  long_description: string | null
  published: number
  status: number
}

export interface TicketTypeSheetPage {
  type: SheetType
  priority: { ID: number; priority_name: string } | null
  workflow: { ID: number; workflow_name: string; steps: number } | null
  statuses: SheetStatus[]
  timers: { take_over: number | null; identify: number | null; intervention: number | null } | null
  counts: { services: number; sla: number; auto_status: number; reopen: number; feedback: number; catalog: number }
  sla_rules: SheetSlaRule[]
  catalog_items: SheetCatalogItem[]
  triggers: { id: number; event_type: string; to_status: number; activated_at: string | null; status_name: string | null }[]
  forms: { own: number; shared: number }
  event_triggers: {
    id: number
    ticket_status_id: number
    company_id: number
    action: string
    action_data: string | null
    status_name: string | null
    company_name: string | null
  }[]
  steps: SheetStep[]
  has_step_assignment: boolean
  /** Campurile formularului fiecarui status (`[]` cand nu are niciunul). */
  status_fields: Record<string, { field_name: string; field_label: string; field_type: string; field_required: number }[]> | never[]
  graph: { nodes: GraphNode[]; edges: GraphEdge[] }
  automations: SheetAutomation[]
  notification_events: { ID: number; event: string; description: string | null; notifications: number }[]
  email_templates: { ID: number; template_name: string; template_subject: string | null }[]
  checks: SheetCheck[]
  history: SheetHistoryRow[]
  sim_priorities: { ID: number; priority_name: string }[]
  sim_categories: { ID: number; category_name: string }[]
  all_priorities: { ID: number; priority_name: string }[]
  chosen_priorities: number[]
  all_workflows: { ID: number; workflow_name: string }[]
  workflow_entity: { ID: number; workflow_id: number; takeover_button: number } | null
  assign_groups: { ID: number; name: string }[]
  /** Pe pas: oamenii grupului scris pe el (plus persoana scrisa, daca e din afara). */
  assign_users: Record<string, { ID: number; user_name: string; outside?: boolean }[]> | never[]
  flow_shared_with: string[]
  update_forms: { ID: number; form_name: string }[]
  update_form: number
  all_services: { ID: number; service_name: string }[]
  type_services: number[]
  trigger_actions: Record<string, string>
  companies: { ID: number; name: string }[]
  in_planning: boolean
  in_escalation: boolean
  groups: { ID: number; department_name: string }[]
  group_dispatch: Record<string, number>
  type_names: Record<string, string>
  all_categories: { ID: number; category_name: string }[]
  type_categories: number[]
  subcategories: { ID: number; subcategory_name: string; category_id?: number }[]
  operations: { ID: number; operation_name: string; subcategory_id?: number }[]
  pane: SheetPane
  companyId: number
}

const BASE = '/settings/easeedesk/ticket_type'

export function ticketTypeSheetQuery(type: number, pane: string) {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'ticket_type_sheet', type],
    queryFn: () => apiGet<TicketTypeSheetPage>(`${BASE}/${type}?pane=${encodeURIComponent(pane)}`),
  })
}

export interface StatusFormPage {
  ticket_type: number
  status: { ID: number; status_name: string; status_color: string; status_type: number } | null
  form: { ID: number; form_name: string } | null
  fields: { field_name: string; field_label: string; field_type: string; field_required: number; field_order: number; is_custom: number }[]
  form_list: { ID: number; form_name: string }[]
  used_by: string[]
  steps: { ID: number; step_name: string; status_to: number; status_name: string | null; status_color: string | null }[]
  shared_with: string[]
  candidates: { ID: number; status_name: string; status_color: string }[]
}

export function statusFormQuery(type: number, status: number) {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'ticket_type_sheet', type, 'status', status],
    queryFn: () => apiGet<StatusFormPage>(`${BASE}/${type}/status/${status}`),
    enabled: status > 0,
  })
}

export interface SheetResult {
  status: 'OK' | 'ERROR' | 'CONFIRM'
  message?: string
  status_id?: number
  form_id?: number
  form_name?: string
  name?: string
  used_by?: string[]
}

export function addFlowStep(type: number, from: number, to: number) {
  return apiPost<SheetResult>(`${BASE}/${type}/flow/step`, { status_from: from, status_to: to })
}

export interface NewStatusFields {
  status_name: string
  status_color: string
  status_type: number
  record_sla: number
}

export function createStatusInFlow(type: number, from: number, fields: NewStatusFields, confirm = false) {
  const post: Record<string, PostValue> = { status_from: from, ...fields }
  if (confirm) post.confirm = 1
  return apiPost<SheetResult>(`${BASE}/${type}/flow/status`, post)
}

export function insertFlowPhase(type: number, step: number, fields: NewStatusFields, confirm = false) {
  const post: Record<string, PostValue> = { step, ...fields }
  if (confirm) post.confirm = 1
  return apiPost<SheetResult>(`${BASE}/${type}/flow/phase`, post)
}

export function removeFlowStep(type: number, step: number) {
  return apiPost<SheetResult>(`${BASE}/${type}/flow/step/remove`, { step_id: step })
}

export interface PaletteField {
  id: number
  custom: number
  name: string
  type: string
  always: number
}

export function statusPaletteQuery(type: number, status: number) {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'ticket_type_sheet', type, 'palette', status],
    queryFn: () => apiGet<SheetResult & { fields?: PaletteField[]; shared_with?: string[] }>(`${BASE}/${type}/status/${status}/palette`),
    enabled: status > 0,
  })
}

export function attachStatusField(type: number, status: number, field: number, custom: boolean) {
  return apiPost<SheetResult>(`${BASE}/${type}/status/${status}/attach`, { field, custom: custom ? 1 : 0 })
}

export function cloneFormForStatus(type: number, status: number, form: number) {
  return apiPost<SheetResult>(`${BASE}/${type}/status/${status}/clone`, { form_id: form })
}

export function createFormForStatus(type: number, status: number, name: string) {
  return apiPost<SheetResult>(`${BASE}/${type}/status/${status}/form`, { form_name: name })
}

export interface SlaSimulation {
  status: 'OK'
  minutes: number | null
  rule: { type: string; category: string; subcategory: string; operation: string; priority: string; minutes: number } | null
}

export function simulateSla(type: number, priority: number, category: number) {
  return apiPost<SlaSimulation>(`${BASE}/${type}/sla`, { priority, category })
}

/** `chosen=on` numai cand e bifat, ca formularul vechi. */
function chosen(on: boolean): Record<string, PostValue> {
  return on ? { chosen: 'on' } : {}
}

export function changeTypeCategory(type: number, category: number, on: boolean) {
  return apiPost<SheetResult>(`${BASE}/${type}/category`, { category_id: category, ...chosen(on) })
}

export function updateCatalogEntry(type: number, entry: number, short: string, long: string, published: boolean) {
  const post: Record<string, PostValue> = { entry, short_description: short, long_description: long }
  if (published) post.published = 'on'
  return apiPost<SheetResult>(`${BASE}/${type}/catalog`, post)
}

export function changeTypeModule(type: number, module: 'planning' | 'escalation', on: boolean) {
  return apiPost<SheetResult>(`${BASE}/${type}/module`, { module, ...chosen(on) })
}

export function changeGroupDispatch(type: number, group: number, on: boolean) {
  return apiPost<SheetResult>(`${BASE}/${type}/dispatch`, { group, ...chosen(on) })
}
